import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import { AppCard } from "../common/appShell";
import InfoPopover from "../common/infoPopover";
import Loading from "../common/loading";
import useDarkMode from "../../hooks/useDarkMode";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  CategoryScale,
);

const OUTLET_TREND_METRICS = {
  current: {
    metricKey: "totalCurrentRms",
    metricLabel: "Current RMS",
    optionLabel: "Current RMS",
    unit: "A",
    color: "#16a34a",
    digits: 2,
  },
  apparentPower: {
    metricKey: "totalApparentPower",
    metricLabel: "Apparent Power",
    optionLabel: "Apparent Power",
    unit: "VA",
    color: "#7c3aed",
    digits: 0,
  },
  voltage: {
    metricKey: "avgCircuitVoltage",
    metricLabel: "Circuit Voltage",
    optionLabel: "Circuit Voltage",
    unit: "V",
    color: "#f97316",
    digits: 1,
  },
};

const TREND_RANGE_OPTIONS = [
  { key: "24h", label: "24 hours", durationMs: 24 * 60 * 60 * 1000, bucketMinutes: 15 },
  { key: "7d", label: "7 days", durationMs: 7 * 24 * 60 * 60 * 1000, bucketMinutes: 120 },
  { key: "30d", label: "30 days", durationMs: 30 * 24 * 60 * 60 * 1000, bucketMinutes: 720 },
];

const TREND_TIME_DISPLAY_FORMATS = {
  minute: "MMM d, h:mm a",
  hour: "MMM d, h a",
  day: "MMM d",
  month: "MMM yyyy",
};

const buildTrendRange = (rangeKey) => {
  const option = TREND_RANGE_OPTIONS.find((item) => item.key === rangeKey) || TREND_RANGE_OPTIONS[0];
  const before = Date.now();
  return {
    since: before - option.durationMs,
    before,
    bucketMinutes: option.bucketMinutes,
  };
};

const hasNumber = (value) =>
  value !== undefined && value !== null && Number.isFinite(Number(value));

const formatNumber = (value, digits = 2) => {
  const number = Number(value || 0);
  return number
    .toFixed(digits)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const formatTrendMetric = (value, metric) => {
  if (!hasNumber(value)) return "-";
  return `${formatNumber(value, metric.digits || 2)}${metric.unit ? ` ${metric.unit}` : ""}`;
};

const formatTrendPercent = (value) => {
  if (!hasNumber(value)) return "-";
  const number = Number(value);
  const sign = number > 0 ? "+" : "";
  return `${sign}${formatNumber(number, 1)}%`;
};

const formatBucketDuration = (minutes) => {
  const numericMinutes = Number(minutes);
  if (!Number.isFinite(numericMinutes) || numericMinutes <= 0) return "latest";
  if (numericMinutes < 60) return `${numericMinutes} min`;
  const hours = numericMinutes / 60;
  return Number.isInteger(hours) ? `${hours} hr` : `${formatNumber(hours, 1)} hr`;
};

const formatDurationMinutes = (minutes) => {
  if (!hasNumber(minutes)) return "-";
  const numericMinutes = Number(minutes);
  if (numericMinutes < 60) return `${formatNumber(numericMinutes, 0)} min`;
  const hours = numericMinutes / 60;
  return Number.isInteger(hours) ? `${hours} hr` : `${formatNumber(hours, 1)} hr`;
};

const resolveTrendTimeUnit = (durationMs) => {
  const hours = durationMs / 3600000;
  if (!Number.isFinite(hours) || hours <= 0) return "hour";
  if (hours <= 36) return "hour";
  if (hours <= 24 * 75) return "day";
  return "month";
};

const withAlpha = (color, alpha) => {
  if (!color || !color.startsWith("#")) return `rgba(22, 163, 74, ${alpha})`;
  const hex = color.slice(1);
  if (hex.length !== 6) return color;
  const value = parseInt(hex, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatTrendDateRange = (since, before) => {
  const options = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
  return `${new Date(since).toLocaleString(undefined, options)} - ${new Date(before).toLocaleString(undefined, options)}`;
};

const resolveOutletIndex = (outlet = {}, explicitOutletIndex) => {
  const value =
    explicitOutletIndex ??
    outlet.pduLabeledOutletNum ??
    outlet.outletNumber ??
    outlet.outletNum ??
    outlet.outletIndex;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const expectedLoadLabel = (expected = {}, anomaly = {}) => {
  if (expected.status !== "ready") return "Learning";
  if (anomaly.state === "near_expected") return "Near expected";
  if (anomaly.state === "low_load") return "Low load";
  if (anomaly.state === "high_load") return "High load";
  if (anomaly.state === "normal") return "Normal";
  return "Learning";
};

const expectedLoadColor = (expected = {}, anomaly = {}) => {
  if (expected.status !== "ready") return "var(--app-text-muted)";
  if (anomaly.state === "normal") return "#047857";
  if (anomaly.state === "near_expected") return "var(--app-text-muted)";
  if (anomaly.severity === "critical") return "#b91c1c";
  if (anomaly.state === "low_load" || anomaly.state === "high_load") return "#b45309";
  return "var(--app-text-muted)";
};

const expectedRangeSubValue = (expected = {}) => {
  if (
    expected.status === "ready" &&
    hasNumber(expected.currentP10) &&
    hasNumber(expected.currentP90)
  ) {
    return `Expected ${formatNumber(expected.currentP10, 2)}-${formatNumber(expected.currentP90, 2)} A`;
  }
  if (expected.status === "too_many_channels") return "Too many channels";
  if (expected.status === "no_channels") return "No channels scoped";
  return "Need more history";
};

const expectedRangeInfo = (expected = {}, anomaly = {}, latestCurrent = null) => {
  if (expected.status !== "ready") {
    return (
      <div style={{ lineHeight: 1.45 }}>
        <div>Expected range needs more profile history.</div>
        <div style={{ marginTop: "0.65rem" }}>
          Learned from the last 30 days for the same UTC hour of week.
        </div>
      </div>
    );
  }

  return (
    <div style={{ lineHeight: 1.45 }}>
      <div>Expected range: {formatNumber(expected.currentP10, 2)}-{formatNumber(expected.currentP90, 2)} A</div>
      <div>Expected median: {formatNumber(expected.currentMedian, 2)} A</div>
      <div>Latest current: {latestCurrent !== null ? `${formatNumber(latestCurrent, 2)} A` : "-"}</div>
      {anomaly.durationMinutes > 0 ? (
        <div>Duration: {formatBucketDuration(anomaly.durationMinutes)}</div>
      ) : null}
      {anomaly.message ? (
        <div style={{ marginTop: "0.65rem" }}>{anomaly.message}</div>
      ) : null}
      <div style={{ marginTop: "0.65rem" }}>
        Learned from the last 30 days for the same UTC hour of week.
      </div>
    </div>
  );
};

const buildExpectedPointSet = (rows, field) =>
  rows
    .map((row) => {
      const x = row.createdAt ? new Date(row.createdAt) : null;
      if (!x || Number.isNaN(x.getTime())) return null;
      return {
        x,
        y: hasNumber(row[field]) ? Number(row[field]) : null,
      };
    })
    .filter(Boolean);

const buildExpectedCurrentDatasets = (rows) => {
  const hasExpectedRange = rows.some(
    (row) => hasNumber(row.expectedCurrentP10) && hasNumber(row.expectedCurrentP90),
  );
  if (!hasExpectedRange) return [];

  const rangeColor = "#64748b";
  return [
    {
      label: "Expected lower",
      data: buildExpectedPointSet(rows, "expectedCurrentP10"),
      borderColor: "rgba(100, 116, 139, 0)",
      backgroundColor: "rgba(100, 116, 139, 0)",
      fill: false,
      pointRadius: 0,
      borderWidth: 0,
      tension: 0.18,
      spanGaps: false,
      parsing: false,
      hideFromLegend: true,
    },
    {
      label: "Expected range",
      data: buildExpectedPointSet(rows, "expectedCurrentP90"),
      borderColor: withAlpha(rangeColor, 0.35),
      backgroundColor: withAlpha(rangeColor, 0.14),
      fill: "-1",
      pointRadius: 0,
      borderWidth: 1,
      tension: 0.18,
      spanGaps: false,
      parsing: false,
    },
    {
      label: "Expected median",
      data: buildExpectedPointSet(rows, "expectedCurrentMedian"),
      borderColor: withAlpha(rangeColor, 0.8),
      borderDash: [4, 4],
      backgroundColor: "rgba(100, 116, 139, 0)",
      fill: false,
      pointRadius: 0,
      borderWidth: 1.5,
      tension: 0.18,
      spanGaps: false,
      parsing: false,
    },
  ];
};

export default function OutletTrendPanel({
  controls,
  outlet = {},
  outletId,
  outletIndex,
  serialNumber,
  subtitle,
  title = "Outlet power trends",
}) {
  const dark = useDarkMode();
  const resolvedOutletId = outletId ?? outlet.outlet_id ?? outlet.id;
  const resolvedOutletIndex = resolveOutletIndex(outlet, outletIndex);
  const [rangeKey, setRangeKey] = useState("24h");
  const [metricKey, setMetricKey] = useState("current");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [refreshIndex, setRefreshIndex] = useState(0);

  const activeMetric = OUTLET_TREND_METRICS[metricKey] || OUTLET_TREND_METRICS.current;
  const fallbackRange = buildTrendRange(rangeKey);
  const parsedSince = Date.parse(meta.since);
  const parsedBefore = Date.parse(meta.before);
  const since = Number.isFinite(parsedSince) ? parsedSince : fallbackRange.since;
  const before = Number.isFinite(parsedBefore) ? parsedBefore : fallbackRange.before;
  const durationMs = Math.max(1, before - since);
  const latest = meta.latest || {};
  const bucketMinutes = Number(meta.bucketMinutes || fallbackRange.bucketMinutes);
  const baselineCurrent = meta.baseline && hasNumber(meta.baseline.totalCurrentRms)
    ? Number(meta.baseline.totalCurrentRms)
    : null;
  const latestCurrent = hasNumber(latest.totalCurrentRms) ? Number(latest.totalCurrentRms) : null;
  const currentChange = hasNumber(meta.currentChangePct) ? Number(meta.currentChangePct) : null;
  const peakApparentPower = hasNumber(meta.peakApparentPower)
    ? Number(meta.peakApparentPower)
    : rows.reduce((peak, row) => {
        const apparentPower = hasNumber(row.totalApparentPower) ? Number(row.totalApparentPower) : null;
        if (apparentPower === null) return peak;
        return peak === null ? apparentPower : Math.max(peak, apparentPower);
      }, null);
  const expected = meta.expected || {};
  const anomaly = meta.anomaly || {};
  const expectedDatasets = useMemo(
    () => (metricKey === "current" ? buildExpectedCurrentDatasets(rows) : []),
    [metricKey, rows],
  );
  const changeColor = currentChange === null
    ? "var(--app-text-muted)"
    : currentChange < -20
      ? "#b91c1c"
      : currentChange < 0
        ? "#b45309"
        : "#047857";
  const loadChangeDirection = currentChange === null
    ? "Not enough current data is available for this timeframe."
    : currentChange < 0
      ? "Current is lower than the earlier average for this timeframe."
      : currentChange > 0
        ? "Current is higher than the earlier average for this timeframe."
        : "Current matches the earlier average for this timeframe.";
  const loadChangeInfo = (
    <div style={{ lineHeight: 1.45 }}>
      <div>Latest {formatBucketDuration(bucketMinutes)} avg: {latestCurrent !== null ? `${formatNumber(latestCurrent, 2)} A` : "-"}</div>
      <div>Earlier avg: {baselineCurrent !== null ? `${formatNumber(baselineCurrent, 2)} A` : "-"}</div>
      <div>Change: {formatTrendPercent(meta.currentChangePct)}</div>
      <div style={{ marginTop: "0.65rem" }}>{loadChangeDirection}</div>
    </div>
  );

  useEffect(() => {
    if (!serialNumber || (!resolvedOutletId && !resolvedOutletIndex)) {
      setRows([]);
      setMeta({});
      setLoaded(true);
      return undefined;
    }

    const range = buildTrendRange(rangeKey);
    const params = new URLSearchParams();
    params.set("enclosure_serial_number", String(serialNumber));
    if (resolvedOutletId) {
      params.set("outlet_id", String(resolvedOutletId));
    }
    if (resolvedOutletIndex) {
      params.set("outlet_index", String(resolvedOutletIndex));
    }
    params.set("since", String(range.since));
    params.set("before", String(range.before));
    params.set("bucketMinutes", String(range.bucketMinutes));

    const cancelSource = axios.CancelToken.source();
    setLoading(true);
    setError("");

    axios
      .get(`/v2/outlets/trends?${params.toString()}`, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        cancelToken: cancelSource.token,
      })
      .then((res) => {
        setRows(res.data?.data || []);
        setMeta(res.data?.meta || {});
        setLoaded(true);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        console.log(err);
        setRows([]);
        setMeta({});
        setLoaded(true);
        setError("Unable to load outlet trends.");
      })
      .finally(() => {
        setLoading(false);
      });

    return () => cancelSource.cancel("Canceling outlet trend request");
  }, [serialNumber, resolvedOutletId, resolvedOutletIndex, rangeKey, refreshIndex]);

  const chartData = useMemo(() => ({
    datasets: [
      ...expectedDatasets,
      {
        label: activeMetric.metricLabel,
        data: rows
          .map((row) => {
            const x = row.createdAt ? new Date(row.createdAt) : null;
            if (!x || Number.isNaN(x.getTime())) return null;
            return {
              x,
              y: hasNumber(row[activeMetric.metricKey])
                ? Number(row[activeMetric.metricKey])
                : null,
            };
          })
          .filter(Boolean),
        borderColor: activeMetric.color,
        backgroundColor: withAlpha(activeMetric.color, 0.16),
        fill: true,
        pointRadius: 0,
        borderWidth: 2,
        tension: 0.18,
        spanGaps: false,
        parsing: false,
      },
    ],
  }), [activeMetric, expectedDatasets, rows]);

  const chartTheme = useMemo(() => ({
    axis: dark ? "#a8b3c2" : "#64748b",
    grid: dark ? "rgba(148, 163, 184, 0.14)" : "rgba(15, 23, 42, 0.08)",
    tooltipBg: dark ? "rgba(15, 23, 42, 0.96)" : "rgba(15, 23, 42, 0.92)",
    tooltipText: "#f8fafc",
  }), [dark]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "nearest", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          color: chartTheme.axis,
          filter: (item, data) => !data.datasets[item.datasetIndex]?.hideFromLegend,
        },
      },
      tooltip: {
        backgroundColor: chartTheme.tooltipBg,
        bodyColor: chartTheme.tooltipText,
        titleColor: chartTheme.tooltipText,
        borderColor: chartTheme.grid,
        borderWidth: 1,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatTrendMetric(context.parsed?.y, activeMetric)}`,
        },
      },
    },
    scales: {
      x: {
        type: "time",
        min: since,
        max: before,
        time: {
          tooltipFormat: "PP p",
          unit: resolveTrendTimeUnit(durationMs),
          displayFormats: TREND_TIME_DISPLAY_FORMATS,
        },
        ticks: { autoSkip: true, color: chartTheme.axis, maxRotation: 0 },
        grid: { color: chartTheme.grid, display: false },
      },
      y: {
        ticks: {
          color: chartTheme.axis,
          callback: (value) => formatTrendMetric(value, activeMetric),
        },
        grid: { color: chartTheme.grid },
      },
    },
  }), [activeMetric, before, chartTheme, durationMs, since]);

  const selectStyle = {
    margin: 0,
    width: "100%",
    padding: "0.35rem 0.5rem",
    borderRadius: "4px",
    border: "1px solid var(--app-control-border)",
    backgroundColor: "var(--app-control-bg)",
    color: "var(--app-control-text)",
  };

  const controlLabelStyle = {
    color: "var(--text-muted-color, #6b7280)",
    display: "flex",
    flexDirection: "column",
    fontSize: "0.78rem",
    fontWeight: 700,
    gap: "0.35rem",
  };

  return (
    <AppCard title={title} description={subtitle}>
      <div className="app-card__section" style={{ paddingTop: 0 }}>
        <div
          style={{
            alignItems: "end",
            display: "grid",
            gap: "0.85rem",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            marginBottom: "1rem",
          }}
        >
          {controls ? <div style={{ minWidth: 0 }}>{controls}</div> : null}
          <label style={controlLabelStyle}>
            <span>Metric</span>
            <select value={metricKey} onChange={(event) => setMetricKey(event.target.value)} style={selectStyle}>
              {Object.entries(OUTLET_TREND_METRICS).map(([key, item]) => (
                <option key={key} value={key}>
                  {item.optionLabel}
                </option>
              ))}
            </select>
          </label>
          <label style={controlLabelStyle}>
            <span>Timeframe</span>
            <select value={rangeKey} onChange={(event) => setRangeKey(event.target.value)} style={selectStyle}>
              {TREND_RANGE_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="button secondary app-toolbar__action-button"
            onClick={() => setRefreshIndex((current) => current + 1)}
            style={{
              justifyContent: "center",
              margin: 0,
              minHeight: "40px",
              width: "100%",
            }}
          >
            <i className="material-icons" style={{ fontSize: "18px" }}>refresh</i>
            <span>Refresh</span>
          </button>
        </div>
        <div
          style={{
            display: "grid",
            gap: "1rem",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            marginBottom: "1rem",
          }}
        >
          <TrendStat
            label="Current now"
            value={formatTrendMetric(latest.totalCurrentRms, OUTLET_TREND_METRICS.current)}
          />
          <TrendStat
            label="Load change"
            value={formatTrendPercent(meta.currentChangePct)}
            color={changeColor}
            subValue={
              meta.loadDropDetected
                ? "Drop detected"
                : baselineCurrent !== null
                  ? `Baseline ${formatNumber(baselineCurrent, 2)} A`
                  : "Vs baseline"
            }
            info={loadChangeInfo}
          />
          <TrendStat
            label="Load state"
            value={expectedLoadLabel(expected, anomaly)}
            color={expectedLoadColor(expected, anomaly)}
            subValue={expectedRangeSubValue(expected)}
            info={expectedRangeInfo(expected, anomaly, latestCurrent)}
          />
          <TrendStat
            label="Zero-current time"
            value={formatDurationMinutes(meta.zeroCurrentDurationMinutes)}
            subValue={
              hasNumber(meta.zeroCurrentDurationMinutes) && Number(meta.zeroCurrentDurationMinutes) > 0
                ? "At or below 0.05 A"
                : "Current above zero threshold"
            }
            info="How long the selected outlet has stayed at or below 0.05 A at the end of this timeframe."
          />
          <TrendStat
            label="Peak apparent power"
            value={hasNumber(peakApparentPower) ? `${formatNumber(peakApparentPower, 0)} VA` : "-"}
            subValue="Highest bucket total"
            info="Highest estimated apparent power observed in one bucket during this selected timeframe. Apparent power is current times circuit voltage."
          />
        </div>
        <div
          style={{
            color: "var(--app-text-muted)",
            display: "flex",
            flexWrap: "wrap",
            fontSize: "0.85rem",
            gap: "1rem",
            justifyContent: "space-between",
            marginBottom: "0.75rem",
          }}
        >
          <span>{formatTrendDateRange(since, before)}</span>
          <span>{bucketMinutes} minute buckets</span>
        </div>
        <div style={{ minHeight: "285px", position: "relative" }}>
          {loading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: dark ? "rgba(21, 27, 36, 0.72)" : "rgba(255, 255, 255, 0.72)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              <Loading />
            </div>
          )}
          <Line options={chartOptions} data={chartData} />
          {!loading && loaded && !error && rows.length === 0 && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                alignItems: "center",
                color: "var(--app-text-muted)",
                display: "flex",
                justifyContent: "center",
              }}
            >
              No trend data for this range.
            </div>
          )}
        </div>
        {error && (
          <div style={{ color: "#b91c1c", fontSize: "0.9rem", marginTop: "0.75rem" }}>
            {error}
          </div>
        )}
      </div>
    </AppCard>
  );
}

const TrendStat = ({ label, value, subValue, color = "var(--app-text)", info }) => (
  <div style={{ borderTop: "1px solid var(--app-border)", paddingTop: "0.75rem" }}>
    <div
      style={{
        alignItems: "center",
        color: "var(--app-text-muted)",
        display: "flex",
        fontSize: "0.78rem",
        fontWeight: 600,
        gap: "0.15rem",
      }}
    >
      <span>{label}</span>
      {info ? <InfoPopover text={info} /> : null}
    </div>
    <div style={{ color, fontWeight: 700, fontSize: "1.2rem", marginTop: "0.25rem" }}>
      {value}
    </div>
    {subValue && (
      <div style={{ color: "var(--app-text-muted)", fontSize: "0.82rem", marginTop: "0.2rem" }}>
        {subValue}
      </div>
    )}
  </div>
);
